import { useRef, useState, useCallback } from 'react';
import { ZoomIn, ZoomOut, Maximize2, RotateCcw } from 'lucide-react';

export default function ZoomImage({ src, alt, testid, aspect = '4 / 3' }) {
  const wrapRef = useRef(null);
  const [t, setT] = useState({ scale: 1, x: 0, y: 0 });
  const [loaded, setLoaded] = useState(false);
  const pointers = useRef(new Map());
  const pinch = useRef(null);
  const last = useRef(null);

  const clamp = (s) => Math.min(4, Math.max(1, s));
  const apply = (scale, x, y) => setT({ scale, x, y });

  const zoomBy = (d) => apply(clamp(t.scale + d), t.x, t.y);
  const reset = () => apply(1, 0, 0);

  const onWheel = (e) => {
    e.preventDefault();
    zoomBy(e.deltaY < 0 ? 0.25 : -0.25);
  };

  const onPointerDown = (e) => {
    e.target.setPointerCapture(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      pinch.current = { dist: Math.hypot(a.x - b.x, a.y - b.y), scale: t.scale };
    }
    last.current = { x: e.clientX, y: e.clientY };
  };

  const onPointerMove = (e) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2 && pinch.current) {
      const [a, b] = [...pointers.current.values()];
      const d = Math.hypot(a.x - b.x, a.y - b.y);
      apply(clamp(pinch.current.scale * (d / pinch.current.dist)), t.x, t.y);
      return;
    }
    if (t.scale > 1 && last.current) {
      apply(t.scale, t.x + (e.clientX - last.current.x), t.y + (e.clientY - last.current.y));
      last.current = { x: e.clientX, y: e.clientY };
    }
  };

  const onPointerUp = (e) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinch.current = null;
    if (t.scale <= 1.02) reset();
  };

  const fullscreen = useCallback(() => {
    const el = wrapRef.current;
    if (!el) return;
    if (document.fullscreenElement) document.exitFullscreen();
    else el.requestFullscreen?.();
  }, []);

  return (
    <div ref={wrapRef} className="group relative select-none overflow-hidden rounded-2xl border border-[#E4E6EB] bg-white" data-testid={testid}>
      {/* loading shimmer — shown until the drawing is ready, never a blank box */}
      {!loaded && (
        <div data-testid={`${testid}-loading`} className="absolute inset-0 z-10 flex min-h-[280px] animate-pulse flex-col items-center justify-center gap-3 bg-[#F4F3EE]">
          <span className="h-10 w-10 animate-spin rounded-full border-[3px] border-[#E4E6EB] border-t-[#C92F4B]" />
          <span className="font-display text-[10px] font-bold uppercase tracking-[0.24em] text-[#646A72]">Loading drawing…</span>
        </div>
      )}
      <div
        className="touch-none"
        style={{ touchAction: 'none' }}
        onWheel={onWheel}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onDoubleClick={() => (t.scale > 1 ? reset() : apply(2.2, t.x, t.y))}
        role="img"
        aria-label={alt}
      >
        <img
          src={src}
          alt={alt}
          draggable="false"
          onLoad={() => setLoaded(true)}
          className="mx-auto max-h-[62vh] w-auto max-w-full object-contain"
          style={{ transform: `translate(${t.x}px, ${t.y}px) scale(${t.scale})`, opacity: loaded ? 1 : 0, transition: 'opacity 0.3s ease, transform 75ms linear' }}
          loading="lazy"
        />
      </div>
      <div className="absolute bottom-3 right-3 flex gap-2">
        <button data-testid={`${testid}-zoom-in`} aria-label="Zoom in" onClick={() => zoomBy(0.4)} className="flex h-11 w-11 items-center justify-center rounded-full border border-[#E4E6EB] bg-white/95 shadow-sm"><ZoomIn size={17} /></button>
        <button data-testid={`${testid}-zoom-out`} aria-label="Zoom out" onClick={() => zoomBy(-0.4)} className="flex h-11 w-11 items-center justify-center rounded-full border border-[#E4E6EB] bg-white/95 shadow-sm"><ZoomOut size={17} /></button>
        <button data-testid={`${testid}-reset`} aria-label="Reset zoom" onClick={reset} className="flex h-11 w-11 items-center justify-center rounded-full border border-[#E4E6EB] bg-white/95 shadow-sm"><RotateCcw size={17} /></button>
        <button data-testid={`${testid}-fullscreen`} aria-label="Open full screen" onClick={fullscreen} className="flex h-11 w-11 items-center justify-center rounded-full bg-[#1C1D21] text-white shadow-sm"><Maximize2 size={17} /></button>
      </div>
      {t.scale > 1 && (
        <span className="absolute left-3 top-3 rounded-full bg-[#1C1D21]/80 px-3 py-1 text-[11px] font-semibold text-white">
          {Math.round(t.scale * 100)}%
        </span>
      )}
    </div>
  );
}
